(() => {
  const SECTION_TYPE = 'custom-article-grid';
  const DESKTOP_MQ = window.matchMedia('(min-width: 1024px)');

  const getSection = (node) => {
    const fromClosest = node?.closest?.(`[data-section-type="${SECTION_TYPE}"]`);
    if (fromClosest) return fromClosest;

    const drawer = node?.closest?.('[data-filter-drawer]');
    const drawerSectionId = drawer?.getAttribute('data-section-id');
    if (drawerSectionId) {
      return document.querySelector(
        `[data-section-type="${SECTION_TYPE}"][data-section-id="${drawerSectionId}"]`
      );
    }

    return document.querySelector(`[data-section-type="${SECTION_TYPE}"]`);
  };

  const getDrawer = (section) => {
    if (!section) return null;
    const sectionId = section.getAttribute('data-section-id');
    const bodyDrawer = document.querySelector(
      `body > [data-filter-drawer][data-section-id="${sectionId}"]`
    );
    if (bodyDrawer) return bodyDrawer;
    return section.querySelector('[data-filter-drawer]');
  };

  const syncSectionFiltersFromDrawer = (section, drawer) => {
    const sidebarForm = section.querySelector('[data-form-type="sidebar"]');
    if (!sidebarForm || !drawer) return;

    drawer.querySelectorAll('[data-filter-input]').forEach((drawerInput) => {
      const handle = drawerInput.getAttribute('data-tag-handle');
      if (!handle) return;
      const sidebarInput = sidebarForm.querySelector(
        `[data-filter-input][data-tag-handle="${CSS.escape(handle)}"]`
      );
      if (sidebarInput) sidebarInput.checked = drawerInput.checked;
    });
  };

  const updateLiveDrawerChrome = (liveDrawer, incomingDrawer) => {
    if (!liveDrawer || !incomingDrawer) return;

    const nextCountHtml = incomingDrawer.querySelector('[data-drawer-results-count]')?.innerHTML;
    const countEl = liveDrawer.querySelector('[data-drawer-results-count]');
    if (countEl && nextCountHtml != null) {
      countEl.innerHTML = nextCountHtml;
    }

    const nextFilters = incomingDrawer.querySelector('.custom-article-grid__active-filters--drawer');
    const liveFilters = liveDrawer.querySelector('.custom-article-grid__active-filters--drawer');
    const liveTop = liveDrawer.querySelector('.custom-article-grid__drawer-top');

    if (nextFilters && liveTop) {
      if (liveFilters) {
        liveFilters.replaceWith(nextFilters.cloneNode(true));
      } else {
        const header = liveTop.querySelector('.custom-article-grid__drawer-header');
        header?.after(nextFilters.cloneNode(true));
      }
    } else if (!nextFilters && liveFilters) {
      liveFilters.remove();
    }
  };

  const belongsToSection = (section, el) => {
    if (!el || !section) return false;
    if (section.contains(el)) return true;
    const drawer = getDrawer(section);
    return Boolean(drawer && drawer.contains(el));
  };

  const queryInSection = (section, selector) => {
    const nodes = Array.from(section.querySelectorAll(selector));
    const drawer = getDrawer(section);
    if (drawer) nodes.push(...drawer.querySelectorAll(selector));
    return nodes;
  };

  const getMarketTag = (section) => {
    const fromSection = section?.getAttribute('data-market-tag');
    if (fromSection) return fromSection;

    const form = queryInSection(section, '[data-filter-form]')[0];
    return form?.getAttribute('data-market-tag') || '';
  };

  const getCheckedHandles = (section) => {
    const inputs = queryInSection(
      section,
      '[data-form-type="sidebar"] [data-filter-input]:checked, [data-form-type="drawer"] [data-filter-input]:checked'
    );
    const handles = [];
    const seen = new Set();

    const marketTag = getMarketTag(section);
    if (marketTag) {
      seen.add(marketTag);
      handles.push(marketTag);
    }

    inputs.forEach((input) => {
      const handle = input.getAttribute('data-tag-handle');
      if (!handle || seen.has(handle)) return;
      seen.add(handle);
      handles.push(handle);
    });

    return handles;
  };

  const SORT_OPTIONS = ['newest', 'oldest', 'title-asc', 'title-desc'];
  const BULK_PAGE_SIZE = 250;
  const catalogs = new WeakMap();

  const getSortValue = (section) => {
    const fromUrl = new URL(window.location.href).searchParams.get('sort');
    if (SORT_OPTIONS.includes(fromUrl)) return fromUrl;

    const fromSection = section?.getAttribute('data-sort');
    if (SORT_OPTIONS.includes(fromSection)) return fromSection;

    return 'newest';
  };

  const getCatalogState = (section) => {
    let state = catalogs.get(section);
    if (!state) {
      state = {
        items: null,
        key: '',
        visibleCount: 0,
        loading: null,
        generation: 0,
        renderId: 0,
      };
      catalogs.set(section, state);
    }
    return state;
  };

  const resetCatalog = (section) => {
    if (!section) return;
    const state = getCatalogState(section);
    state.items = null;
    state.key = '';
    state.visibleCount = 0;
    state.loading = null;
    state.generation += 1;
    state.renderId += 1;
  };

  const getCatalogKey = (section) => {
    const url = new URL(buildFilterUrl(section), window.location.origin);
    url.searchParams.delete('sort');
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    url.searchParams.delete('article_bulk');
    return `${url.pathname}${url.search}`;
  };

  const getPageSize = (section) => {
    const fromSection = Number(section?.getAttribute('data-per-page'));
    return fromSection > 0 ? fromSection : 8;
  };

  const getResultsCount = (section) => {
    const fromSection = Number(section?.getAttribute('data-results-count'));
    if (fromSection > 0) return fromSection;

    const strongs = section?.querySelector('[data-pagination-item-count]')?.querySelectorAll('strong');
    const fromFooter = Number(strongs?.[1]?.textContent);
    if (fromFooter > 0) return fromFooter;

    return section?.querySelectorAll('[data-article-item]')?.length || 0;
  };

  const compareItems = (sort) => (a, b) => {
    const aPublished = Number(a.getAttribute('data-published') || 0);
    const bPublished = Number(b.getAttribute('data-published') || 0);
    const aTitle = a.getAttribute('data-title') || '';
    const bTitle = b.getAttribute('data-title') || '';
    const locale = document.documentElement.lang || undefined;

    if (sort === 'oldest') return aPublished - bPublished;
    if (sort === 'title-asc') return aTitle.localeCompare(bTitle, locale);
    if (sort === 'title-desc') return bTitle.localeCompare(aTitle, locale);
    return bPublished - aPublished;
  };

  const withSortParam = (url, sort) => {
    const next = new URL(url, window.location.origin);
    if (!sort || sort === 'newest') {
      next.searchParams.delete('sort');
    } else {
      next.searchParams.set('sort', sort);
    }
    return `${next.pathname}${next.search}${next.hash}`;
  };

  let blogCategoryDefaults = null;

  const getBlogCategories = (section) => {
    const script = section?.querySelector('[data-blog-categories]');
    if (!script) return [];
    try {
      const parsed = JSON.parse(script.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error(error);
      return [];
    }
  };

  const getTaggedHandlesFromUrl = () => {
    const match = window.location.pathname.match(/\/tagged\/([^/?#]+)/);
    if (!match) return [];
    return decodeURIComponent(match[1])
      .split('+')
      .map((handle) => handle.trim().toLowerCase())
      .filter(Boolean);
  };

  const ensureMarketTaggedUrl = (section) => {
    if (window.Shopify?.designMode) return false;
    const marketTag = getMarketTag(section);
    if (!marketTag) return false;
    if (getTaggedHandlesFromUrl().includes(marketTag.toLowerCase())) return false;

    const nextUrl = buildFilterUrl(section);
    const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (!nextUrl || nextUrl === current) return false;

    window.location.replace(nextUrl);
    return true;
  };

  const findMatchingCategory = (section) => {
    const categories = getBlogCategories(section);
    if (!categories.length) return null;

    const handles = getTaggedHandlesFromUrl();
    if (!handles.length) return null;

    return categories.find((cat) => handles.includes(String(cat.tag || '').toLowerCase())) || null;
  };

  const getBlogHeadingEl = () =>
    document.querySelector('[data-blog-heading]') ||
    document.querySelector('.template-blog .section-blocks__heading') ||
    document.querySelector('.section-blocks__heading');

  const ensureBlogCategoryDefaults = (section) => {
    if (blogCategoryDefaults) return blogCategoryDefaults;

    const heading = getBlogHeadingEl();
    const list = document.querySelector('.custom-breadcrumbs .breadcrumbs__list, .breadcrumbs__list');
    const chevron = list?.querySelector('.breadcrumbs__icon')?.outerHTML || '';
    const defaultHeading =
      heading?.getAttribute('data-blog-heading-default')?.trim() ||
      heading?.textContent?.trim() ||
      '';

    blogCategoryDefaults = {
      headingText: defaultHeading,
      blogTitle: section?.getAttribute('data-blog-title') || '',
      blogUrl: section?.getAttribute('data-blog-url') || '',
      chevronHtml: chevron,
    };

    return blogCategoryDefaults;
  };

  const setBlogHeading = (title) => {
    const heading = getBlogHeadingEl();
    if (heading && title != null) heading.textContent = title;
  };

  const renderDefaultBlogBreadcrumb = (defaults) => {
    const list = document.querySelector('.custom-breadcrumbs .breadcrumbs__list, .breadcrumbs__list');
    if (!list || !defaults.blogTitle) return;

    const homeItem = list.querySelector('.breadcrumbs__item');
    if (!homeItem) return;

    Array.from(list.querySelectorAll('.breadcrumbs__item')).slice(1).forEach((item) => item.remove());

    const root = document.createElement('li');
    root.className = 'breadcrumbs__item';
    root.setAttribute('data-blog-breadcrumb-root', '');

    const current = document.createElement('span');
    current.className = 'breadcrumbs__current';
    current.setAttribute('aria-current', 'page');
    current.setAttribute('data-blog-breadcrumb-current', '');
    current.textContent = defaults.blogTitle;

    root.appendChild(current);
    list.appendChild(root);
  };

  const renderCategoryBlogBreadcrumb = (defaults, categoryTitle) => {
    const list = document.querySelector('.custom-breadcrumbs .breadcrumbs__list, .breadcrumbs__list');
    if (!list || !defaults.blogTitle) return;

    const homeItem = list.querySelector('.breadcrumbs__item');
    if (!homeItem) return;

    Array.from(list.querySelectorAll('.breadcrumbs__item')).slice(1).forEach((item) => item.remove());

    const blogItem = document.createElement('li');
    blogItem.className = 'breadcrumbs__item';

    const blogLink = document.createElement('a');
    blogLink.className = 'breadcrumbs__link';
    blogLink.href = defaults.blogUrl;
    blogLink.setAttribute('data-blog-breadcrumb-blog', '');
    blogLink.textContent = defaults.blogTitle;
    blogItem.appendChild(blogLink);

    if (defaults.chevronHtml) {
      blogItem.insertAdjacentHTML('beforeend', defaults.chevronHtml);
    }

    const categoryItem = document.createElement('li');
    categoryItem.className = 'breadcrumbs__item';
    categoryItem.setAttribute('data-blog-breadcrumb-category', '');

    const current = document.createElement('span');
    current.className = 'breadcrumbs__current';
    current.setAttribute('aria-current', 'page');
    current.setAttribute('data-blog-breadcrumb-current', '');
    current.textContent = categoryTitle;

    categoryItem.appendChild(current);
    list.appendChild(blogItem);
    list.appendChild(categoryItem);
  };

  const syncBlogCategoryUI = (section) => {
    const target = section || getSection(document.body);
    if (!target) return;

    const defaults = ensureBlogCategoryDefaults(target);
    const category = findMatchingCategory(target);

    if (category) {
      setBlogHeading(category.title || defaults.headingText);
      renderCategoryBlogBreadcrumb(defaults, category.title || '');
    } else {
      if (defaults.headingText) setBlogHeading(defaults.headingText);
      renderDefaultBlogBreadcrumb(defaults);
    }
  };

  const buildFilterUrl = (section) => {
    const blogUrl = section.getAttribute('data-blog-url') || '';
    const handles = getCheckedHandles(section);
    const path = handles.length ? `${blogUrl}/tagged/${handles.join('+')}` : blogUrl;
    return withSortParam(path, getSortValue(section));
  };

  const withSectionId = (url, sectionId) => {
    const next = new URL(url, window.location.origin);
    next.searchParams.set('section_id', sectionId);
    return next.toString();
  };

  const sortGrid = (section) => {
    const grid = section.querySelector('[data-article-grid]');
    if (!grid) return;

    const items = Array.from(grid.querySelectorAll('[data-article-item]'));
    items.sort(compareItems(getSortValue(section)));
    items.forEach((item) => grid.appendChild(item));
  };

  const updatePaginationFooter = (section, visibleCount, totalCount) => {
    const countEl = section.querySelector('[data-pagination-item-count]');
    if (countEl) {
      const strongs = countEl.querySelectorAll('strong');
      if (strongs.length >= 2) {
        strongs[0].textContent = String(visibleCount);
        strongs[1].textContent = String(totalCount);
      }
    }

    const wrapper = section.querySelector('[data-show-more-wrapper]');
    if (!wrapper) return;

    if (visibleCount >= totalCount) {
      wrapper.innerHTML = '';
      return;
    }

    if (wrapper.querySelector('[data-show-more]')) return;

    const template = section.querySelector('[data-show-more-template]');
    if (template?.content) {
      wrapper.appendChild(template.content.cloneNode(true));
    }
  };

  const renderFromCatalog = (section, { append = false } = {}) => {
    const grid = section.querySelector('[data-article-grid]');
    const state = getCatalogState(section);
    if (!grid || !state.items) return;

    const sorted = state.items.slice().sort(compareItems(getSortValue(section)));
    const total = sorted.length;
    const pageSize = getPageSize(section);

    if (!append) {
      state.visibleCount = 0;
      while (grid.firstChild) grid.removeChild(grid.firstChild);
    }

    const nextCount = Math.min(state.visibleCount + pageSize, total);
    sorted.slice(state.visibleCount, nextCount).forEach((item) => {
      grid.appendChild(item);
    });
    state.visibleCount = nextCount;

    updatePaginationFooter(section, state.visibleCount, total);
  };

  const loadCatalog = async (section) => {
    const state = getCatalogState(section);
    const key = getCatalogKey(section);
    if (state.items && state.key === key) return state.items;
    if (state.loading && state.key === key) return state.loading;

    const sectionId = section.getAttribute('data-section-id');
    const total = getResultsCount(section);
    const baseUrl = buildFilterUrl(section);
    const generation = state.generation;
    state.key = key;

    const fetchBulkPage = (page) => {
      const url = new URL(baseUrl, window.location.origin);
      url.searchParams.delete('sort');
      url.searchParams.set('article_bulk', '1');
      url.searchParams.set('page', String(page));
      return fetchSection(url.toString(), sectionId);
    };

    const collectItems = (incoming, items, seen) => {
      incoming.querySelectorAll('[data-article-item]').forEach((item) => {
        const id = item.getAttribute('data-article-id') || item.getAttribute('data-title');
        if (id && seen.has(id)) return;
        if (id) seen.add(id);
        items.push(item);
      });
    };

    const throwIfStale = () => {
      if (state.generation !== generation) {
        const error = new Error('stale catalog');
        error.stale = true;
        throw error;
      }
    };

    const promise = (async () => {
      const items = [];
      const seen = new Set();
      const estimatedPages = Math.max(1, Math.ceil((total || BULK_PAGE_SIZE) / BULK_PAGE_SIZE));
      const firstBatch = await Promise.all(
        Array.from({ length: estimatedPages }, (_, index) => fetchBulkPage(index + 1))
      );
      throwIfStale();
      firstBatch.forEach((incoming) => collectItems(incoming, items, seen));

      let page = estimatedPages + 1;
      while (total > 0 && items.length < total && page <= 40) {
        const incoming = await fetchBulkPage(page);
        throwIfStale();
        const before = items.length;
        collectItems(incoming, items, seen);
        if (items.length === before) break;
        page += 1;
      }

      state.items = items;
      if (state.loading === promise) state.loading = null;
      return items;
    })();

    state.loading = promise;
    return promise;
  };

  const applyCurrentSort = async (section) => {
    if (!section) return;

    syncSortUI(section);
    const sort = getSortValue(section);
    const state = getCatalogState(section);

    if (sort === 'newest' && !state.items) {
      sortGrid(section);
      return;
    }

    const generation = state.generation;
    state.renderId += 1;
    const renderId = state.renderId;

    setLoading(section, true);
    try {
      await loadCatalog(section);
      if (state.renderId !== renderId || state.generation !== generation) return;
      renderFromCatalog(section, { append: false });
    } catch (error) {
      if (error?.stale) return;
      console.error(error);
      if (state.renderId === renderId) sortGrid(section);
    } finally {
      if (state.renderId === renderId) setLoading(section, false);
    }
  };

  const syncSortUI = (section) => {
    const sort = getSortValue(section);
    section.setAttribute('data-sort', sort);

    section.querySelectorAll('[data-sort-option]').forEach((option) => {
      const isActive = option.getAttribute('data-sort-value') === sort;
      option.classList.toggle('is-active', isActive);
      option.setAttribute('aria-selected', String(isActive));
    });
  };

  const closeSortMenu = (section) => {
    const toggle = section.querySelector('[data-sort-toggle]');
    const menu = section.querySelector('[data-sort-menu]');
    if (!toggle || !menu) return;
    toggle.setAttribute('aria-expanded', 'false');
    menu.setAttribute('aria-hidden', 'true');
  };

  const toggleSortMenu = (section) => {
    const toggle = section.querySelector('[data-sort-toggle]');
    const menu = section.querySelector('[data-sort-menu]');
    if (!toggle || !menu) return;

    const expanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!expanded));
    menu.setAttribute('aria-hidden', String(expanded));
  };

  const applySort = (section, sortValue) => {
    if (!SORT_OPTIONS.includes(sortValue)) return;

    section.setAttribute('data-sort', sortValue);
    closeSortMenu(section);

    const next = new URL(withSortParam(window.location.href, sortValue), window.location.origin);
    next.searchParams.delete('page');
    history.replaceState({ customArticleGrid: true }, '', `${next.pathname}${next.search}${next.hash}`);

    applyCurrentSort(section);
  };

  const parseSectionHtml = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (
      doc.querySelector(`[data-section-type="${SECTION_TYPE}"]`) ||
      doc.querySelector('[data-custom-article-grid-partial]')?.closest('div') ||
      doc.body
    );
  };

  const syncSectionMeta = (section, incoming) => {
    if (!section || !incoming) return;
    ['data-results-count', 'data-per-page'].forEach((attr) => {
      const value = incoming.getAttribute?.(attr);
      if (value != null) section.setAttribute(attr, value);
    });
  };

  const replacePartial = (section, incoming) => {
    const currentPartial = section.querySelector('[data-custom-article-grid-partial]');
    const nextPartial = incoming.querySelector('[data-custom-article-grid-partial]');
    if (!currentPartial || !nextPartial) {
      section.replaceWith(incoming);
      return getSection(document.body);
    }
    syncSectionMeta(section, incoming);
    currentPartial.replaceWith(nextPartial);
    return section;
  };

  const setLoading = (section, isLoading) => {
    section.classList.toggle('is-loading', isLoading);
  };

  const fetchSection = async (url, sectionId) => {
    const response = await fetch(withSectionId(url, sectionId), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!response.ok) throw new Error(`Failed to fetch section: ${response.status}`);
    const html = await response.text();
    return parseSectionHtml(html);
  };

  const navigateFilters = async (section, options = {}) => {
    const sectionId = section.getAttribute('data-section-id');
    const url = buildFilterUrl(section);
    const liveDrawer = document.querySelector(
      `body > [data-filter-drawer][data-section-id="${sectionId}"]`
    );
    const keepDrawerOpen =
      Boolean(options.keepDrawerOpen) ||
      (liveDrawer != null && liveDrawer.getAttribute('aria-hidden') === 'false');

    setLoading(section, true);
    resetCatalog(section);

    try {
      const sort = getSortValue(section);
      const incoming = await fetchSection(url, sectionId);
      const incomingDrawer = incoming.querySelector('[data-filter-drawer]');

      if (keepDrawerOpen && liveDrawer) {
        updateLiveDrawerChrome(liveDrawer, incomingDrawer);

        // Keep the open drawer mounted; strip drawers from the incoming markup
        incoming.querySelectorAll('[data-filter-drawer]').forEach((el) => el.remove());

        const nextSection = replacePartial(section, incoming);
        nextSection?.querySelectorAll('[data-filter-drawer]').forEach((el) => el.remove());

        if (nextSection) {
          nextSection.setAttribute('data-sort', sort);
          syncSectionFiltersFromDrawer(nextSection, liveDrawer);
          nextSection.querySelector('[data-drawer-toggle]')?.setAttribute('aria-expanded', 'true');
        }

        liveDrawer.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('scroll-lock');

        history.pushState({ customArticleGrid: true }, '', url);
        await bindSection(nextSection);
      } else {
        if (liveDrawer?.parentElement === document.body) {
          liveDrawer.remove();
        }

        const nextSection = replacePartial(section, incoming);
        if (nextSection) {
          nextSection.setAttribute('data-sort', sort);
        }
        history.pushState({ customArticleGrid: true }, '', url);
        await bindSection(nextSection);
        document.documentElement.classList.remove('scroll-lock');
      }
    } catch (error) {
      console.error(error);
      window.location.href = url;
    } finally {
      setLoading(section, false);
    }
  };

  const syncInputs = (section, sourceInput) => {
    const handle = sourceInput.getAttribute('data-tag-handle');
    if (!handle) return;

    queryInSection(section, `[data-filter-input][data-tag-handle="${handle}"]`).forEach((input) => {
      if (input === sourceInput) return;
      input.checked = sourceInput.checked;
    });

    queryInSection(section, '[data-filter-group]').forEach((group) => {
      const hasActive = group.querySelector('[data-filter-input]:checked') != null;
      group.setAttribute('data-filter-group-has-active', String(hasActive));
      const clearBtn = group.querySelector('[data-filter-group-clear]');
      if (clearBtn) clearBtn.setAttribute('data-has-active-filters', String(hasActive));
    });
  };

  const clearAll = (section) => {
    queryInSection(section, '[data-filter-input]').forEach((input) => {
      input.checked = false;
    });
    queryInSection(section, '[data-filter-group]').forEach((group) => {
      group.setAttribute('data-filter-group-has-active', 'false');
      const clearBtn = group.querySelector('[data-filter-group-clear]');
      if (clearBtn) clearBtn.setAttribute('data-has-active-filters', 'false');
    });
    navigateFilters(section);
  };

  const clearGroup = (group, section) => {
    group.querySelectorAll('[data-filter-input]').forEach((input) => {
      input.checked = false;
      syncInputs(section, input);
    });
    navigateFilters(section);
  };

  const getDrawerHost = (section) =>
    section.querySelector('[data-filter-drawer-host]') || section.querySelector('[data-custom-article-grid-partial]') || section;

  const openDrawer = (section) => {
    const drawer = getDrawer(section);
    const toggle = section.querySelector('[data-drawer-toggle]');
    if (!drawer) return;

    // Escape section stacking contexts (animations/transforms) so drawer sits above header
    if (drawer.parentElement !== document.body) {
      drawer.setAttribute('data-section-id', section.getAttribute('data-section-id') || '');
      document.body.appendChild(drawer);
    }

    drawer.setAttribute('aria-hidden', 'false');
    toggle?.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('scroll-lock');
  };

  const closeDrawer = (section) => {
    const drawer = getDrawer(section) || document.querySelector('.custom-article-grid__drawer[aria-hidden="false"]');
    const toggle = section?.querySelector('[data-drawer-toggle]');
    if (!drawer) return;

    drawer.setAttribute('aria-hidden', 'true');
    toggle?.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('scroll-lock');

    const host = section ? getDrawerHost(section) : null;
    if (host && drawer.parentElement === document.body) {
      // Return after close transition so it stays in section markup for AJAX replace
      window.setTimeout(() => {
        if (drawer.getAttribute('aria-hidden') === 'true' && host.isConnected) {
          host.appendChild(drawer);
        }
      }, 320);
    }
  };

  const toggleSidebar = (section) => {
    const sidebar = section.querySelector('[data-filter-sidebar]');
    const toggle = section.querySelector('[data-sidebar-toggle]');
    if (!sidebar || !toggle) return;

    const isHidden = sidebar.getAttribute('aria-hidden') === 'true';
    sidebar.setAttribute('aria-hidden', isHidden ? 'false' : 'true');
    toggle.setAttribute('aria-expanded', isHidden ? 'true' : 'false');
  };

  const toggleFilterGroup = (button) => {
    const panelId = button.getAttribute('aria-controls');
    const panel = panelId ? document.getElementById(panelId) : button.closest('[data-filter-group]')?.querySelector('[data-filter-group-panel]');
    if (!panel) return;

    const expanded = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', String(!expanded));
    panel.setAttribute('aria-hidden', String(expanded));
  };

  const showMore = async (section, button) => {
    const catalogState = getCatalogState(section);
    if (catalogState.items) {
      renderFromCatalog(section, { append: true });
      return;
    }

    const sectionId = section.getAttribute('data-section-id');
    const rawNextUrl = button.getAttribute('data-next-url') || button.getAttribute('href');
    if (!rawNextUrl || rawNextUrl === '#') return;

    const nextUrl = withSortParam(rawNextUrl, getSortValue(section));

    setLoading(section, true);

    try {
      const incoming = await fetchSection(nextUrl, sectionId);
      const currentGrid = section.querySelector('[data-article-grid]');
      const nextGrid = incoming.querySelector('[data-article-grid]');
      const nextItems = nextGrid ? Array.from(nextGrid.querySelectorAll('[data-article-item]')) : [];

      if (currentGrid && nextItems.length) {
        nextItems.forEach((item) => currentGrid.appendChild(item));
        sortGrid(section);
      }

      const currentCount = section.querySelector('[data-pagination-item-count]');
      const nextCount = incoming.querySelector('[data-pagination-item-count]');
      if (currentCount && nextCount) {
        currentCount.innerHTML = nextCount.innerHTML;
      }

      const currentWrapper = section.querySelector('[data-show-more-wrapper]');
      const nextWrapper = incoming.querySelector('[data-show-more-wrapper]');
      if (currentWrapper && nextWrapper) {
        currentWrapper.innerHTML = nextWrapper.innerHTML;
      } else if (currentWrapper) {
        currentWrapper.innerHTML = '';
      }
    } catch (error) {
      console.error(error);
      window.location.href = nextUrl;
    } finally {
      setLoading(section, false);
    }
  };

  const onClick = (event) => {
    const section = getSection(event.target);
    if (!section) return;

    const sortToggle = event.target.closest('[data-sort-toggle]');
    if (sortToggle && belongsToSection(section, sortToggle)) {
      event.preventDefault();
      toggleSortMenu(section);
      return;
    }

    const sortOption = event.target.closest('[data-sort-option]');
    if (sortOption && belongsToSection(section, sortOption)) {
      event.preventDefault();
      applySort(section, sortOption.getAttribute('data-sort-value'));
      return;
    }

    const sidebarToggle = event.target.closest('[data-sidebar-toggle]');
    if (sidebarToggle && belongsToSection(section, sidebarToggle)) {
      event.preventDefault();
      closeSortMenu(section);
      toggleSidebar(section);
      return;
    }

    const drawerToggle = event.target.closest('[data-drawer-toggle]');
    if (drawerToggle && belongsToSection(section, drawerToggle)) {
      event.preventDefault();
      openDrawer(section);
      return;
    }

    const applyFiltersBtn = event.target.closest('[data-apply-filters]');
    if (applyFiltersBtn && belongsToSection(section, applyFiltersBtn)) {
      event.preventDefault();
      navigateFilters(section);
      return;
    }

    const closeDrawerBtn = event.target.closest('[data-close-drawer]');
    if (closeDrawerBtn && belongsToSection(section, closeDrawerBtn)) {
      event.preventDefault();
      closeDrawer(section);
      return;
    }

    const groupToggle = event.target.closest('[data-filter-group-toggle]');
    if (groupToggle && belongsToSection(section, groupToggle)) {
      event.preventDefault();
      toggleFilterGroup(groupToggle);
      return;
    }

    const groupClear = event.target.closest('[data-filter-group-clear]');
    if (groupClear && belongsToSection(section, groupClear)) {
      event.preventDefault();
      const group = groupClear.closest('[data-filter-group]');
      if (group) clearGroup(group, section);
      return;
    }

    const removeFilterBtn = event.target.closest('[data-remove-filter]');
    if (removeFilterBtn && belongsToSection(section, removeFilterBtn)) {
      event.preventDefault();
      const handle = removeFilterBtn.getAttribute('data-tag-handle');
      if (handle) {
        queryInSection(section, `[data-filter-input][data-tag-handle="${handle}"]`).forEach((input) => {
          input.checked = false;
          syncInputs(section, input);
        });
      }
      navigateFilters(section);
      return;
    }

    const clearAllBtn = event.target.closest('[data-clear-all-filters]');
    if (clearAllBtn && belongsToSection(section, clearAllBtn)) {
      event.preventDefault();
      clearAll(section);
      return;
    }

    const showMoreBtn = event.target.closest('[data-show-more]');
    if (showMoreBtn && belongsToSection(section, showMoreBtn)) {
      event.preventDefault();
      showMore(section, showMoreBtn);
    }
  };

  let filterDebounce;

  const onChange = (event) => {
    const input = event.target.closest('[data-filter-input]');
    if (!input) return;

    const section = getSection(input);
    if (!section) return;

    syncInputs(section, input);

    const formType = input.closest('[data-filter-form]')?.getAttribute('data-form-type');
    const keepDrawerOpen = formType === 'drawer' && !DESKTOP_MQ.matches;

    clearTimeout(filterDebounce);
    filterDebounce = setTimeout(
      () => navigateFilters(section, { keepDrawerOpen }),
      350
    );
  };

  const onPopState = async () => {
    const section = getSection(document.body);
    if (!section) return;

    const sectionId = section.getAttribute('data-section-id');
    setLoading(section, true);

    try {
      resetCatalog(section);
      const incoming = await fetchSection(window.location.href, sectionId);
      replacePartial(section, incoming);
      await bindSection(getSection(document.body));
    } catch (error) {
      console.error(error);
      window.location.reload();
    } finally {
      setLoading(section, false);
    }
  };

  let bound = false;

  const bindSection = (section) => {
    const target = section || getSection(document.body);
    if (!target) return Promise.resolve();
    syncBlogCategoryUI(target);
    return applyCurrentSort(target);
  };

  const onDocumentClick = (event) => {
    if (event.target.closest('[data-sort-group]')) return;

    document.querySelectorAll(`[data-section-type="${SECTION_TYPE}"]`).forEach((section) => {
      closeSortMenu(section);
    });
  };

  const init = () => {
    const section = getSection(document.body);
    if (section && ensureMarketTaggedUrl(section)) return;

    if (!bound) {
      bound = true;
      document.addEventListener('click', onClick);
      document.addEventListener('click', onDocumentClick);
      document.addEventListener('change', onChange);
      window.addEventListener('popstate', onPopState);
    }

    bindSection();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target?.querySelector?.(`[data-section-type="${SECTION_TYPE}"]`) ||
        event.target?.matches?.(`[data-section-type="${SECTION_TYPE}"]`)) {
      init();
    }
  });
})();
